import { TAG_GROUPS, LEGACY_TAG_GROUPS } from "@/features/avatar-tags/contract";
import {
  plazaCategories,
  plazaPopularQueries,
  plazaSorts,
  type PlazaFilters,
} from "@/features/plaza/types";

type SearchValue = string | string[] | undefined;
export type PlazaSearchParams = Record<string, SearchValue>;

function first(value: SearchValue) {
  return Array.isArray(value) ? value[0] : value;
}

function positiveInteger(value: SearchValue) {
  const parsed = Number(first(value));
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export function parsePlazaFilters(params: PlazaSearchParams): PlazaFilters {
  const rawCategory = first(params.category);
  const categoryValue = rawCategory && /^(男|女)(网红|素人)$/u.test(rawCategory) ? `${rawCategory[0]}形象` : rawCategory;
  const sortValue = first(params.sort);
  const requestedTags = Array.isArray(params.tag) ? params.tag : [params.tag];
  const knownTags = new Set([...TAG_GROUPS, ...LEGACY_TAG_GROUPS].flatMap(group => group.options));
  const tags = [...new Set(requestedTags.filter((tag): tag is string => typeof tag === "string" && knownTags.has(tag)))].slice(0, 6);
  const requestedPopular = Array.isArray(params.hot) ? params.hot : [params.hot];
  const popular = [...new Set(requestedPopular.filter((value): value is string => typeof value === "string" && plazaPopularQueries.some(query => query === value)))];
  const numeric: Pick<PlazaFilters, "minPrice" | "maxPrice" | "minFans"> = {};
  for (const key of ["minPrice", "maxPrice", "minFans"] as const) { const v = first(params[key]); if (v && /^\d{1,9}(\.\d{1,2})?$/u.test(v)) numeric[key] = Number(v); }
  return {
    ...numeric,
    ...(popular.length ? { popular } : {}),
    query: (first(params.q) ?? "").trim().slice(0, 80),
    ...(tags.length ? { tags } : {}),
    category: plazaCategories.find((item) => item === categoryValue),
    sort: plazaSorts.find((item) => item === sortValue) ?? "recommend",
    page: Math.min(positiveInteger(params.page) ?? 1, 10_000),
    pageSize: 12,
  };
}

export function plazaSearchParams(
  filters: PlazaFilters,
  overrides: Partial<PlazaFilters> = {},
) {
  const next = { ...filters, ...overrides };
  const params = new URLSearchParams();
  for (const key of ["minPrice", "maxPrice", "minFans"] as const) if (next[key] !== undefined) params.set(key, String(next[key]));
  if (next.query) params.set("q", next.query);
  next.popular?.forEach(query => params.append("hot", query));
  next.tags?.forEach(tag => params.append("tag", tag));
  if (next.category) params.set("category", next.category);
  if (next.sort !== "recommend") params.set("sort", next.sort);
  if (next.page > 1) params.set("page", String(next.page));
  const query = params.toString();
  return query ? `/plaza?${query}` : "/plaza";
}
